//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion


import { CommonHelper, ToastHelper, LocalizationHelper, ConvertHelper, LogHelper } from '../../helpers'

import { OpOperator, OpOperatorData, OpActor, OpDataTypeInGroup, OpRole, OpActivity, OpOperatorTechnicalPermission } from '../../models/core'
import { Enums } from '../../models/common'
import { OpActorData } from '../../models/custom'
import { IReferenceType, IOpObject, IOpData } from '../../models/util/interfaces'
import { OpDataList } from '../../models/util'

import { BulkEditInitParams, CreateEditInitParams } from '../../modules/common/create-edit-popup/create-edit-popup.controller'

import { USER_SESSION_SERVICE_NAME, UserSessionService } from '../user-session'
import { DataTypeGroupService, DATA_TYPE_GROUP_SERVICE_NAME } from '../data-type'
import { OperatorService } from './operator.service'

//#region Consts

export const OPERATOR_CREATE_EDIT_SERVICE_NAME: string = 'OperatorCreateEditService'
export const OPERATOR_CREATE_EDIT_SERVICE_PROVIDER_NAME: string = 'OperatorCreateEditServiceProvider'

//#endregion

export class OperatorCreateEditService {

    //#region Consts

    private get LOG_PREFIX(): string { return '[SIMAX.Services.OperatorCreateEdit]' }
    private get COPY_ID_DATA_TYPES_TO_RESET(): Enums.DataType[] {
        return [
            Enums.DataType.HELPER_ID_OPERATOR,
            Enums.DataType.HELPER_LOGIN,
            Enums.DataType.HELPER_PASSWORD,
            Enums.DataType.HELPER_ID_ACTOR,
        ]
    }

    private get EMAIL_NOTIFICATION_ID_DATA_TYPES_TO_COPY(): Enums.DataType[] {
        return [
            Enums.DataType.OPERATOR_EMAIL_NOTIFICATION_OPTIONS_LOCATION,
            Enums.DataType.OPERATOR_EMAIL_NOTIFICATION_OPTIONS_ISSUE,
            Enums.DataType.OPERATOR_EMAIL_NOTIFICATION_OPTIONS_TASK,
            Enums.DataType.OPERATOR_EMAIL_NOTIFICATION_OPTIONS_ROUTE,
            Enums.DataType.OPERATOR_EMAIL_NOTIFICATION_OPTIONS_PACKAGE,
            Enums.DataType.OPERATOR_EMAIL_NOTIFICATION_OPTIONS_SHIPPING_LIST_CLOSE,
            Enums.DataType.OPERATOR_EMAIL_NOTIFICATION_OPTIONS_TASK_ID_DISTRIBUTOR,
            Enums.DataType.OPERATOR_EMAIL_NOTIFICATION_OPTIONS_ISSUE_ID_DISTRIBUTOR,
            Enums.DataType.OPERATOR_EMAIL_NOTIFICATION_OPTIONS_LOCATION_ID_DISTRIBUTOR,
            Enums.DataType.OPERATOR_EMAIL_NOTIFICATION_OPTIONS_PACKAGE_ID_DISTRIBUTOR,
        ]
    }

    private get SMS_NOTIFICATION_ID_DATA_TYPES_TO_COPY(): Enums.DataType[] {
        return [
            Enums.DataType.OPERATOR_SMS_NOTIFICATION_OPTIONS_LOCATION,
            Enums.DataType.OPERATOR_SMS_NOTIFICATION_OPTIONS_ISSUE,
            Enums.DataType.OPERATOR_SMS_NOTIFICATION_OPTIONS_TASK,
            Enums.DataType.OPERATOR_SMS_NOTIFICATION_OPTIONS_ROUTE,
            Enums.DataType.OPERATOR_SMS_NOTIFICATION_OPTIONS_PACKAGE,
            Enums.DataType.OPERATOR_SMS_NOTIFICATION_OPTIONS_SHIPPING_LIST_CLOSE,
            Enums.DataType.OPERATOR_SMS_NOTIFICATION_OPTIONS_TASK_ID_DISTRIBUTOR,
            Enums.DataType.OPERATOR_SMS_NOTIFICATION_OPTIONS_ISSUE_ID_DISTRIBUTOR,
            Enums.DataType.OPERATOR_SMS_NOTIFICATION_OPTIONS_LOCATION_ID_DISTRIBUTOR,
        ]
    }

    //#endregion


    //#region Ctor

    constructor(
        private dataTypeGroupService: DataTypeGroupService,
        private userSessionService: UserSessionService
    ) { }

    //#endregion


    //#region Bulk

    public createBulkOperatorFromIOpObject(opObject: IOpObject): OpOperator {
        const operator = OpOperator.fromIOpObject(opObject)
        const isOperatorModified: boolean = _.some(opObject.dataList.toArray(), (data: OpOperatorData) => _.include([Enums.OpChangeState.New, Enums.OpChangeState.Modified], data.opState))

        operator.opState = isOperatorModified ? Enums.OpChangeState.Modified : Enums.OpChangeState.Loaded
        operator.actor = new OpActor(null)

        return operator
    }

    public getBulkCreateEditOptions(gridData: any[], columns: any[]): BulkEditInitParams {
        return {
            gridColumns: columns,
            groupReferenceType: null,
            groupReferenceValue: null,
            idReferenceType: OperatorService.REFERENCE_TYPE,
            moduleName: OperatorService.SIMAX_MODULE_NAME,
            section: null,
            selectedGridRows: gridData,
            useBusinessObjects: true,
        }
    }

    //#endregion


    //#region Copy

    public createCopyIOpObjectFromOperator(sourceOperator: OpOperator, operatorDataTypes: OpDataTypeInGroup[] = [], actorDataTypes: OpDataTypeInGroup[] = []): IOpObject & IReferenceType {
        const opObject: IOpObject & IReferenceType = this.createIOpObjectFromOperator(sourceOperator, operatorDataTypes, actorDataTypes)
        const actorIdDataTypes: number[] = actorDataTypes.map((dataType: OpDataTypeInGroup) => dataType.idDataType)

        _.forEach(opObject.dataList.toArray(), (data: OpOperatorData) => {
            if (_.contains(this.COPY_ID_DATA_TYPES_TO_RESET, data.idDataType) || _.contains(actorIdDataTypes, data.idDataType)) {
                data.value = null
            }
        });

        return opObject
    }

    public createCopyOperatorFromIOpObject(sourceOperator: OpOperator, sourceIOpObject: IOpObject, operatorDataTypes: OpDataTypeInGroup[], actorDataTypes: OpDataTypeInGroup[]): OpOperator {
        //copy profile from source operator if there is no such dataType in editGroup
        if (_.find(operatorDataTypes, (data: OpDataTypeInGroup) => {
            return data.idDataType === Enums.DataType.PROFILE_ID
        }) == null) {
            const dtToGroup: OpDataTypeInGroup = new OpDataTypeInGroup()
            dtToGroup.idDataType = Enums.DataType.PROFILE_ID

            operatorDataTypes.push(dtToGroup)

            if (sourceIOpObject.dataList.getData(Enums.DataType.PROFILE_ID, 0) == null) {
                sourceIOpObject.dataList.setValue(sourceOperator.dataList.getValue(Enums.DataType.PROFILE_ID), Enums.DataType.PROFILE_ID, 0)
            }
        }

        const operator: OpOperator = this.createOperatorFromIOpObject(sourceIOpObject, operatorDataTypes, actorDataTypes)

        operator.idOperator = 0
        operator.idActor = 0
        operator.opState = Enums.OpChangeState.New
        operator.actor.idActor = 0
        operator.actor.opState = Enums.OpChangeState.New

        operator.emailNotificationOptions = sourceOperator.emailNotificationOptions.clone()
        operator.smsNotificationOptions = sourceOperator.smsNotificationOptions.clone()

        _.forEach([...this.EMAIL_NOTIFICATION_ID_DATA_TYPES_TO_COPY, ...this.SMS_NOTIFICATION_ID_DATA_TYPES_TO_COPY], (emailDataType: Enums.DataType) => {
            const operatorData: OpOperatorData = sourceOperator.dataList.getData(emailDataType, 0)

            if (operatorData != null) {
                operator.dataList.push(operatorData.clone())
            }
        })

        if (operator.dataList != null) {
            _.forEach(operator.dataList.toArray(), (data: OpOperatorData) => {
                data.idOperator = 0
                data.idOperatorData = 0
                data.opState = Enums.OpChangeState.New
            })
        }

        if (operator.actor.dataList != null) {
            _.forEach(operator.actor.dataList.toArray(), (data: OpActorData) => {
                data.idActor = 0
                data.idActorData = 0
                data.opState = Enums.OpChangeState.New
            })
        }

        operator.allActivities = _
            .filter(sourceOperator.allActivities, (activity: OpActivity) => {
                return activity.idOperatorActivity != null
            })
            .map((activity: OpActivity) => {
                const clonedActivity: OpActivity = activity.clone(true, true, true, true)

                clonedActivity.idOperatorActivity = 0
                clonedActivity.opState = Enums.OpChangeState.New

                return clonedActivity
            })

        operator.roles = _.map(sourceOperator.roles, (role: OpRole) => {
            const clonedRole: OpRole = role.clone(true, true, true, true)

            clonedRole.opState = Enums.OpChangeState.New

            return clonedRole
        })

        operator.operatorTechnicalPermissions = _.map(sourceOperator.operatorTechnicalPermissions, (operatorTechnicalPermission: OpOperatorTechnicalPermission) => {
            const clonedOperatorTechnicalPermission: OpOperatorTechnicalPermission = operatorTechnicalPermission.clone(true, true, true, true)

            clonedOperatorTechnicalPermission.idOperator = 0
            clonedOperatorTechnicalPermission.idOperatorTechnicalPermission = 0
            clonedOperatorTechnicalPermission.opState = Enums.OpChangeState.New

            return clonedOperatorTechnicalPermission
        })


        return operator
    }

    //#endregion


    //#region Edit

    public createIOpObjectFromOperator(operator: OpOperator, operatorDataTypes: OpDataTypeInGroup[] = [], actorDataTypes: OpDataTypeInGroup[] = []): IOpObject & IReferenceType {
        if (operator == null) {
            return null
        }

        const dataList: OpDataList<IOpData> = new OpDataList<IOpData>();

        _.forEach(operatorDataTypes, (dataTypeInGroup: OpDataTypeInGroup) => {
            const operatorData: OpOperatorData = operator.dataList.getData(dataTypeInGroup.idDataType, 0)

            if (operatorData != null) {
                dataList.setValue(operatorData.value, operatorData.idDataType, 0)
            }
        })

        _.forEach(actorDataTypes, (dataTypeInGroup: OpDataTypeInGroup) => {
            const actorData: OpActorData = operator.actor.dataList.getData(dataTypeInGroup.idDataType, 0)

            if (actorData != null) {
                dataList.setValue(actorData.value, actorData.idDataType, 0)
            }
        })

        const opObject: IOpObject & IReferenceType = {
            customFormatString: null,
            dataList,
            dynamicValues: null,
            idObject: operator.idOperator,
            opState: Enums.OpChangeState.Loaded,

            getReferenceKey() {
                return this.idObject;
            },

            getReferenceType() {
                return Enums.ReferenceType.IdOperator
            },

            getReferenceValue() {
                return this
            },

            fromJSON: () => { },
            toJSON: () => { },
        }

        return opObject
    }

    public createOperatorFromIOpObject(opObject: IOpObject, operatorDataTypes: OpDataTypeInGroup[], actorDataTypes: OpDataTypeInGroup[]): OpOperator {
        if (opObject == null) {
            return null
        }

        let operatorData: OpOperatorData[] = []
        let actorData: OpActorData[] = []

        if (_.isArray(operatorDataTypes)) {
            const idDataTypes: number[] = _.map(operatorDataTypes, (dataTypeInGroup: OpDataTypeInGroup) => dataTypeInGroup.idDataType)

            operatorData = _.chain(opObject.dataList.toArray())
                .filter((data: IOpData) => _.contains(idDataTypes, data.idDataType))
                .map((data: IOpData) => OpOperatorData.fromIOpData(data))
                .value()
        }

        if (_.isArray(actorDataTypes)) {
            const idDataTypes: number[] = _.map(actorDataTypes, (dataTypeInGroup: OpDataTypeInGroup) => dataTypeInGroup.idDataType)

            actorData = _.chain(opObject.dataList.toArray())
                .filter((data: IOpData) => _.contains(idDataTypes, data.idDataType))
                .map((data: IOpData) => OpActorData.fromIOpData(data))
                .value()
        }

        const newOperator: OpOperator = new OpOperator(null)
        const isOperatorModified: boolean = _.some(operatorData, (data: OpOperatorData) => _.include([Enums.OpChangeState.New, Enums.OpChangeState.Modified], data.opState))
        const isActorModified: boolean = _.some(actorData, (data: OpActorData) => _.include([Enums.OpChangeState.New, Enums.OpChangeState.Modified], data.opState))

        newOperator.idOperator = opObject.idObject
        newOperator.opState = Enums.OpChangeState.New
        newOperator.dataList.push(...operatorData)

        newOperator.actor = new OpActor(null)
        newOperator.actor.opState = Enums.OpChangeState.New
        newOperator.actor.dataList.push(...actorData)

        if (newOperator.idOperator > 0) {
            newOperator.opState = isOperatorModified ? Enums.OpChangeState.Modified : Enums.OpChangeState.Loaded
            newOperator.actor.opState = isActorModified ? Enums.OpChangeState.Modified : Enums.OpChangeState.Loaded
        }

        return newOperator
    }

    //#endregion


    //#region Parameters

    public getOperatorEditDataTypeGroup(): JQueryPromise<OpDataTypeInGroup[]> {
        return this.dataTypeGroupService.getDataTypeInGroupByCategory([
            Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__PANEL_PARAMS_TO_EDIT,
            Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__PARAMS_REQUIRED,
        ], OperatorService.SIMAX_MODULE_NAME)
    }

    public getActorEditDataTypeGroup(): JQueryPromise<OpDataTypeInGroup[]> {
        // TO DO From actor service
        return this.dataTypeGroupService.getDataTypeInGroupByCategory([
            Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__PANEL_PARAMS_TO_EDIT,
            Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__PARAMS_REQUIRED,
        ], Enums.SimaxModuleName.Actor)
    }

    //#endregion


    //#region Options

    public getCreateEditOptions(referenceObjectId: number = 0, referenceObject: IReferenceType & IOpObject = null, copy: boolean = false): CreateEditInitParams {
        const options: CreateEditInitParams = {
            createEditSelectedTabType: Enums.SimaxCreateEditSelectedTabType.BaseParameters,
            groupReferenceType: null,
            groupReferenceValue: null,
            idReferenceType: OperatorService.REFERENCE_TYPE,
            moduleName: OperatorService.SIMAX_MODULE_NAME,
            sections: null,
            useBusinessObjects: true,

            referenceObject,
            referenceObjectId,
        }

        options.isCopy = copy

        return options
    }

    //#endregion


    //#region Save

    public saveOperator(operator: OpOperator, isBulkSave: boolean = false): JQueryPromise<OpOperator> {
        _.forEach(operator.dataList.toArray(), (data: OpOperatorData) => {
            if (data.value != null && data.idDataType === Enums.DataType.HELPER_PASSWORD && (data.opState === Enums.OpChangeState.New || data.opState === Enums.OpChangeState.Modified)) {
                data.value = ConvertHelper.hash(data.value)
            }
        })

        return this.userSessionService
            .getDataProvider().Operator
            .saveOperator(operator)
            .then((savedObject: OpOperator) => {
                if (!isBulkSave) {
                    ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVED, OperatorService.SIMAX_MODULE_NAME))
                    CommonHelper.setValueToLocalStorage(CommonHelper.localStorageVarForSavedEntity.userSaved, true, 0)
                }

                return savedObject
            })
            .fail((error: any) => {
                if (!isBulkSave) {
                    ToastHelper.showError(ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVE_ERROR, OperatorService.SIMAX_MODULE_NAME))
                    LogHelper.warn(OperatorService.LOG_PREFIX, `Unable to save operator with id ${operator.idOperator}.`, error)
                }

                return $.Deferred().reject(error).promise()
            })
    }

    //#endregion
}

export class OperatorCreateEditServiceProvider implements ng.IServiceProvider {

    //#region IServiceProvider Implementation 

    public $get = [
        DATA_TYPE_GROUP_SERVICE_NAME,
        USER_SESSION_SERVICE_NAME,
        function (dataTypeGroupService: DataTypeGroupService, userSession: UserSessionService) {
            return new OperatorCreateEditService(dataTypeGroupService, userSession)
        }]

    //#endregion
} 